import React, { useEffect, useRef, useState } from 'react';
import { Camera, X } from 'lucide-react';

const MAX_SOURCE_BYTES = 5 * 1024 * 1024;
const MAX_PROFILE_IMAGE_BYTES = 40 * 1024;
const MAX_STREAM_PROFILE_IMAGE_BYTES = 4.8 * 1024;
const OUTPUT_SIZE = 192;
const STREAM_OUTPUT_SIZE = 96;

const bytesForDataUrl = (dataUrl = '') => {
  const base64 = dataUrl.split(',')[1] || '';
  return Math.ceil((base64.length * 3) / 4);
};

const getDisplayName = (settings) =>
  `${settings?.firstName || ''} ${settings?.lastName || ''}`.trim() || settings?.email || 'User';

const ProfileEditorModal = ({ open, userSettings, onClose, onSave }) => {
  const fileInputRef = useRef(null);
  const cropRef = useRef(null);
  const dragRef = useRef(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [image, setImage] = useState('');
  const [sourceImage, setSourceImage] = useState('');
  const [imageMeta, setImageMeta] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setFirstName(userSettings?.firstName || '');
    setLastName(userSettings?.lastName || '');
    setImage(userSettings?.profilePicture || '');
    setSourceImage('');
    setImageMeta(null);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setError('');
  }, [open, userSettings]);

  if (!open) return null;

  const handleFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Choose an image file.');
      return;
    }
    if (file.size > MAX_SOURCE_BYTES) {
      setError('Choose an image under 5 MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        setSourceImage(reader.result);
        setImageMeta({ width: img.naturalWidth, height: img.naturalHeight });
        setZoom(1);
        setOffset({ x: 0, y: 0 });
        setError('');
      };
      img.onerror = () => setError('Could not read that image.');
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const handlePointerDown = (event) => {
    if (!sourceImage) return;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startOffset: offset,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current) return;
    const dx = event.clientX - dragRef.current.startX;
    const dy = event.clientY - dragRef.current.startY;
    setOffset({
      x: dragRef.current.startOffset.x + dx,
      y: dragRef.current.startOffset.y + dy,
    });
  };

  const handlePointerUp = () => {
    dragRef.current = null;
  };

  const drawCroppedImage = (img, sx, sy, sw, sh, size, maxBytes, initialQuality) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, size, size);

    let quality = initialQuality;
    let dataUrl = canvas.toDataURL('image/jpeg', quality);
    while (bytesForDataUrl(dataUrl) > maxBytes && quality > 0.25) {
      quality -= 0.1;
      dataUrl = canvas.toDataURL('image/jpeg', quality);
    }
    if (bytesForDataUrl(dataUrl) > maxBytes) {
      throw new Error('Could not compress image enough. Try a simpler photo.');
    }
    return dataUrl;
  };

  const buildCroppedImages = async () => {
    if (!sourceImage || !imageMeta || !cropRef.current) {
      if (image && bytesForDataUrl(image) > MAX_PROFILE_IMAGE_BYTES) {
        const img = new Image();
        img.src = image;
        await img.decode();
        const side = Math.min(img.naturalWidth, img.naturalHeight);
        const sx = Math.max(0, (img.naturalWidth - side) / 2);
        const sy = Math.max(0, (img.naturalHeight - side) / 2);
        return {
          profilePicture: drawCroppedImage(img, sx, sy, side, side, OUTPUT_SIZE, MAX_PROFILE_IMAGE_BYTES, 0.84),
          profilePictureThumb: drawCroppedImage(img, sx, sy, side, side, STREAM_OUTPUT_SIZE, MAX_STREAM_PROFILE_IMAGE_BYTES, 0.78),
        };
      }
      return {
        profilePicture: image || '',
        profilePictureThumb: userSettings?.profilePictureThumb || '',
      };
    }

    const cropSize = cropRef.current.getBoundingClientRect().width;
    const coverScale = Math.max(cropSize / imageMeta.width, cropSize / imageMeta.height) * zoom;
    const displayedWidth = imageMeta.width * coverScale;
    const displayedHeight = imageMeta.height * coverScale;
    const imageLeft = (cropSize - displayedWidth) / 2 + offset.x;
    const imageTop = (cropSize - displayedHeight) / 2 + offset.y;
    const sx = Math.max(0, -imageLeft / coverScale);
    const sy = Math.max(0, -imageTop / coverScale);
    const sw = Math.min(imageMeta.width - sx, cropSize / coverScale);
    const sh = Math.min(imageMeta.height - sy, cropSize / coverScale);

    const img = new Image();
    img.src = sourceImage;
    await img.decode();

    return {
      profilePicture: drawCroppedImage(img, sx, sy, sw, sh, OUTPUT_SIZE, MAX_PROFILE_IMAGE_BYTES, 0.84),
      profilePictureThumb: drawCroppedImage(img, sx, sy, sw, sh, STREAM_OUTPUT_SIZE, MAX_STREAM_PROFILE_IMAGE_BYTES, 0.78),
    };
  };

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const cropped = await buildCroppedImages();
      await onSave({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        name: `${firstName.trim()} ${lastName.trim()}`,
        profilePicture: cropped.profilePicture || null,
        profilePictureThumb: cropped.profilePictureThumb || null,
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  };

  const previewName = `${firstName} ${lastName}`.trim() || getDisplayName(userSettings);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 px-4 backdrop-blur-sm">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Edit Profile</h2>
            <p className="text-sm text-slate-500">Name and profile photo</p>
          </div>
          <button className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="space-y-5 px-5 py-5">
          <div className="flex flex-col items-center gap-3">
            <div
              ref={cropRef}
              className="relative h-48 w-48 overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-200"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
            >
              {sourceImage ? (
                <img
                  src={sourceImage}
                  alt=""
                  draggable={false}
                  className="absolute left-1/2 top-1/2 max-w-none select-none"
                  style={{
                    width: imageMeta ? imageMeta.width * Math.max(192 / imageMeta.width, 192 / imageMeta.height) * zoom : 192,
                    height: imageMeta ? imageMeta.height * Math.max(192 / imageMeta.width, 192 / imageMeta.height) * zoom : 192,
                    transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                    cursor: 'grab',
                  }}
                />
              ) : image ? (
                <img src={image} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-4xl font-semibold text-slate-500">
                  {previewName.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Camera size={16} />
              Choose Photo
            </button>
            {sourceImage ? (
              <label className="w-full max-w-xs text-xs font-semibold uppercase tracking-wide text-slate-500">
                Zoom
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.01"
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="mt-2 w-full accent-orange-500"
                />
              </label>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-sm font-medium text-slate-700">
              First name
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 outline-none focus:border-orange-400" />
            </label>
            <label className="text-sm font-medium text-slate-700">
              Last name
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 outline-none focus:border-orange-400" />
            </label>
          </div>

          {error ? <div className="rounded-lg border border-red-100 bg-red-50 p-3 text-sm text-red-600">{error}</div> : null}
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-200 px-5 py-4">
          <button className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileEditorModal;
